import type { BoardController } from "@rosette/ts/src/business/game/controller/board/BoardController.js";
import type { DiceController } from "@rosette/ts/src/business/game/controller/dice/DiceController.js";
import { AnalysisGameController } from "@rosette/ts/src/business/game/controller/AnalysisGameController.js";
import type { GameController } from "@rosette/ts/src/business/game/controller/GameController.js";
import type { GameDirective } from "@rosette/ts/src/business/game/controller/GameDirective.js";
import { PlayGameController } from "@rosette/ts/src/business/game/controller/PlayGameController.js";
import type { PlayerStateController } from "@rosette/ts/src/business/game/controller/playerstate/PlayerStateController.js";
import type { ReactionController } from "@rosette/ts/src/business/game/controller/reactions/ReactionController.js";
import { ReplayGameController } from "@rosette/ts/src/business/game/controller/ReplayGameController.js";
import { AnalysisGameSource } from "@rosette/ts/src/business/game/controller/source/AnalysisGameSource.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import { PerfectGameSource } from "@rosette/ts/src/business/game/controller/source/PerfectGameSource.js";
import { SelfPlayComputerGameSource } from "@rosette/ts/src/business/game/controller/source/SelfPlayComputerGameSource.js";
import type { GameThemeType } from "@rosette/ts/src/business/game/theme/GameThemeType.js";
import type { ControllerProviderType } from "./createControllerContext.js";
import { createControllerContext, useCreateController } from "./createControllerContext.js";

interface GameControllerContextDependencies {
    useGameSource: () => GameSource;
    usePlayerStateController: () => PlayerStateController;
    useReactionController: () => ReactionController;
    useDiceController: () => DiceController;
    useBoardController: () => BoardController;
    getDefaultThemeType: () => GameThemeType;
}

export function createGameControllerContext({
    useGameSource,
    usePlayerStateController,
    useReactionController,
    useDiceController,
    useBoardController,
    getDefaultThemeType,
}: GameControllerContextDependencies): {
    GameControllerProvider: ControllerProviderType;
    useGameController: () => GameController;
    useOptionalGameController: () => GameController | null;
    useGameDirective: () => GameDirective;
    usePlayGameController: () => PlayGameController;
    useAnalysisGameController: () => AnalysisGameController;
    usePerfectGameController: () => ReplayGameController;
} {
    const useCreateGameController = () => {
        const gameSource = useGameSource();
        const playerStateController = usePlayerStateController();
        const reactionController = useReactionController();
        const diceController = useDiceController();
        const boardController = useBoardController();
        const defaultThemeType = getDefaultThemeType();

        return useCreateController<GameDirective, GameController>(() => {
            if (gameSource instanceof AnalysisGameSource) {
                return new AnalysisGameController(
                    playerStateController, reactionController,
                    diceController, boardController, gameSource,
                    defaultThemeType,
                );
            } else if (gameSource instanceof PerfectGameSource) {
                return new ReplayGameController(
                    playerStateController, reactionController,
                    diceController, boardController, gameSource,
                    defaultThemeType,
                );
            } else {
                const skipDiceRolls = gameSource instanceof SelfPlayComputerGameSource;
                return new PlayGameController(
                    playerStateController, reactionController,
                    diceController, boardController, gameSource,
                    defaultThemeType, skipDiceRolls,
                );
            }
        }, [
            playerStateController, reactionController,
            diceController, boardController, gameSource,
        ]);
    };

    const {
        ControllerProvider,
        useController,
        useOptionalController,
        useDirective,
    } = createControllerContext<GameDirective, GameController>(useCreateGameController);

    function usePlayGameController(): PlayGameController {
        const controller = useController();
        if (controller instanceof PlayGameController)
            return controller;

        throw new Error("Current controller is not a PlayGameController");
    }

    function useAnalysisGameController(): AnalysisGameController {
        const controller = useController();
        if (controller instanceof AnalysisGameController)
            return controller;

        throw new Error("Current controller is not an AnalysisGameController");
    }

    function usePerfectGameController(): ReplayGameController {
        const controller = useController();
        if (controller instanceof ReplayGameController)
            return controller;

        throw new Error("Current controller is not a PerfectGameController");
    }

    return {
        GameControllerProvider: ControllerProvider,
        useGameController: useController,
        useOptionalGameController: useOptionalController,
        useGameDirective: useDirective,
        usePlayGameController,
        useAnalysisGameController,
        usePerfectGameController,
    };
}
